import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDButton from "components/MDButton";
import api from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

/**
 * Mostra um menu ligado ao utilizador:
 * - Se autenticado: opção "Terminar sessão" (logout).
 * - Se não autenticado: opção "Iniciar sessão" (navega para /auth/login).
 *
 * Aceita opcionalmente `items` para continuar a listar PDFs (se quiseres manter essa funcionalidade).
 */
export default function UserMenu({
  basePath = "/docs",
  items, // [{ label, file }]
}) {
  const navigate = useNavigate();
  const { isAuthenticated, logout, userData } = useAuth();
  const { showAlert } = useAlert();

  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const [openImagemDialog, setOpenImagemDialog] = useState(false);
  const [imgErro, setImgErro] = useState(false);
  const [imgTick, setImgTick] = useState(() => Date.now());
  const [canCamera, setCanCamera] = useState(false);

  const defaultItems = useMemo(() => [], []);
  const list = items?.length ? items : defaultItems;

  const open = Boolean(anchorEl);
  const onOpen = (e) => {
    setImgErro(false);
    setAnchorEl(e.currentTarget);
  };

  const onClose = () => setAnchorEl(null);

  const userImgUrl =
    isAuthenticated && userData?.id
      ? `/Uploads/Utilizadores/${userData.id}/Imagens/${userData.id}.jpg?t=${imgTick}`
      : "";

  const getIniciais = (nome) => {
    if (!nome) return "?";

    const ignorar = ["da", "de", "do", "dos", "das", "e"];

    const parts = String(nome)
      .toLowerCase()
      .trim()
      .split(" ")
      .filter((p) => p && !ignorar.includes(p));

    if (parts.length === 0) return "?";

    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const iniciais = getIniciais(userData?.designacao);

  useEffect(() => {
    try {
      const ua = String(navigator.userAgent || "");
      const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
      setCanCamera(isMobile);
    } catch {
      setCanCamera(false);
    }
  }, []);

  useEffect(() => {
    setImgErro(false);
    setImgTick(Date.now());
  }, [userData?.id]);

  const handleOpenPdf = (file) => {
    const url = `${basePath}/${encodeURIComponent(file)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  const handleLogin = () => {
    onClose();
    navigate("/auth/login");
  };

  const handleOpenImagemDialog = () => {
    onClose();
    setOpenImagemDialog(true);
  };

  const handleAskLogout = () => {
    onClose();
    setOpenDialog(true);
  };

  const compressImageIfNeeded = async (file) => {
    try {
      if (!file) return file;

      const type = String(file.type || "").toLowerCase();
      const name = String(file.name || "").toLowerCase();

      const isImg =
        type.startsWith("image/") ||
        name.endsWith(".jpg") ||
        name.endsWith(".jpeg") ||
        name.endsWith(".png") ||
        name.endsWith(".webp");

      if (!isImg) return file;

      if (file.size <= 350 * 1024) return file;

      const MAX_DIM = 1600;
      const QUALITY = 0.75;

      let bitmap;
      if ("createImageBitmap" in window) {
        bitmap = await createImageBitmap(file);
      } else {
        bitmap = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });
      }

      const w = bitmap.width;
      const h = bitmap.height;

      let nw = w;
      let nh = h;

      if (w >= h && w > MAX_DIM) {
        nw = MAX_DIM;
        nh = Math.round((h * MAX_DIM) / w);
      } else if (h > w && h > MAX_DIM) {
        nh = MAX_DIM;
        nw = Math.round((w * MAX_DIM) / h);
      }

      const canvas = document.createElement("canvas");
      canvas.width = nw;
      canvas.height = nh;

      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.drawImage(bitmap, 0, 0, nw, nh);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
      if (!blob) return file;

      const base = file.name?.replace(/\.[^/.]+$/, "") || "imagem";
      const newName = `${base}.jpg`;

      const compressed = new File([blob], newName, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      if (compressed.size >= file.size) return file;

      return compressed;
    } catch {
      return file;
    }
  };

  const handleUploadFoto = async (file) => {
    if (!file || !userData?.id) return;

    const fileFinal = await compressImageIfNeeded(file);

    const formData = new FormData();
    formData.append("id", String(userData.id));
    formData.append("file", fileFinal);

    try {
      const res = await api.post("UtilizadoresAcessos/UploadUtilizadorImagem", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const resposta = res?.data;
      if (!resposta?.success) {
        showAlert(resposta?.message || "Erro ao carregar imagem.", "error");
        return;
      }

      setImgErro(false);
      setImgTick(Date.now());
      showAlert(resposta?.message || "Imagem gravada com sucesso.", "success");

      onClose();
    } catch (err) {
      showAlert(err?.message || "Erro ao carregar imagem.", "error");
    }
  };

  const handleDoLogout = async () => {
    setOpenDialog(false);
    await logout();
    navigate("/auth/login");
  };

  return (
    <>
      <Tooltip title={isAuthenticated ? userData?.designacao || "Utilizador" : "Iniciar sessão"}>
        <IconButton
          size="small"
          onClick={onOpen}
          sx={{
            ml: 1,
            p: 0, // 🔥 remove padding interno (CRÍTICO)
            width: 36,
            height: 36,
            borderRadius: "50%", // garante círculo perfeito
            "&:hover": { bgcolor: "rgba(255,152,0,0.10)" },
          }}
        >
          {isAuthenticated && userData?.id && !imgErro ? (
            <img
              src={userImgUrl}
              alt="Utilizador"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                objectFit: "cover",
                display: "block",
                //border: "2px solid #fff",
                //boxShadow: "0 0 0 1px rgba(0,0,0,0.2)",
              }}
              onError={() => setImgErro(true)}
            />
          ) : (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f57c00", // laranja do botão atual
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                //border: "2px solid #fff",
                //boxShadow: "0 0 0 1px rgba(0,0,0,0.2)",
              }}
            >
              {iniciais}
            </div>
          )}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        keepMounted
        MenuListProps={{ dense: true }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            borderRadius: 2,
            minWidth: 260,
            "& .MuiMenuItem-root": { py: 0.5 },
            "& .MuiListItemIcon-root": { minWidth: 28 },
            "& .MuiListItemText-primary": { fontSize: 14.5 },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {list.length > 0 && (
          <>
            {list.map((it) => (
              <MenuItem key={it.file || it.label} onClick={() => handleOpenPdf(it.file)}>
                <ListItemIcon>
                  <Icon fontSize="small">picture_as_pdf</Icon>
                </ListItemIcon>
                <ListItemText primary={it.label} />
              </MenuItem>
            ))}
            <Divider sx={{ my: 0.5 }} />
          </>
        )}

        {isAuthenticated ? (
          <>
            <div
              style={{
                padding: "10px 16px 8px 16px",
                fontSize: 12,
                color: "#7b809a",
                fontWeight: 500,
                lineHeight: 1.3,
              }}
            >
              {userData?.designacao || "Utilizador"}
            </div>

            <Divider sx={{ my: 0.5 }} />

            <MenuItem onClick={handleOpenImagemDialog}>
              <ListItemIcon>
                <Icon fontSize="small">photo_camera</Icon>
              </ListItemIcon>
              <ListItemText primary="Alterar imagem" />
            </MenuItem>

            <MenuItem onClick={handleAskLogout}>
              <ListItemIcon>
                <Icon fontSize="small">logout</Icon>
              </ListItemIcon>
              <ListItemText primary="Terminar sessão" />
            </MenuItem>
          </>
        ) : (
          <MenuItem onClick={handleLogin}>
            <ListItemIcon>
              <Icon fontSize="small">login</Icon>
            </ListItemIcon>
            <ListItemText primary="Iniciar sessão" />
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={openImagemDialog}
        onClose={() => setOpenImagemDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Alterar imagem</DialogTitle>
        <DialogContent dividers>
          {canCamera && (
            <MenuItem component="label" sx={{ borderRadius: 2, mb: 1 }}>
              <ListItemIcon>
                <Icon fontSize="small">photo_camera</Icon>
              </ListItemIcon>
              <ListItemText primary="Tirar foto" />
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  setOpenImagemDialog(false);
                  if (file) handleUploadFoto(file);
                }}
              />
            </MenuItem>
          )}

          <MenuItem component="label" sx={{ borderRadius: 2 }}>
            <ListItemIcon>
              <Icon fontSize="small">upload</Icon>
            </ListItemIcon>
            <ListItemText primary="Escolher ficheiro" />
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                setOpenImagemDialog(false);
                if (file) handleUploadFoto(file);
              }}
            />
          </MenuItem>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenImagemDialog(false)} variant="outlined" color="secondary">
            Cancelar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Confirmação de logout */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Terminar sessão</DialogTitle>
        <DialogContent dividers>Tem a certeza que pretende terminar a sessão?</DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenDialog(false)} variant="outlined" color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleDoLogout} variant="contained" color="info">
            Sair
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
